import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { CredentialsBar } from './components/CredentialsBar';
import { LiveChat } from './components/LiveChat';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { ScrollToTopButton } from './components/ScrollToTopButton';
import { PageTransition } from './components/PageTransition';
import { Home } from './pages/Home';
import { Contact } from './pages/Contact';
import { Product } from './pages/Product';
import { HelpMeChoose } from './pages/HelpMeChoose';
import { AboutPage } from './pages/AboutPage';
import { DeliveryZones } from './pages/DeliveryZones';
import { Subcontractors } from './pages/Subcontractors';
import { DiyHelp } from './pages/DiyHelp';
import { DeliveryPrep } from './pages/DeliveryPrep';
import { CompareMaterials } from './pages/CompareMaterials';
import { NotFound } from './pages/NotFound';
import { CartProvider } from './components/CartContext';
import { CartDrawer } from './components/CartDrawer';
import { AnnouncementBar } from './components/AnnouncementBar';
import { I18nProvider } from './utils/i18n';
import { TrackOrder } from './pages/TrackOrder';
import { TrackOrderLookup } from './pages/TrackOrderLookup';
import { Calculator } from './pages/Calculator';
import { LocalVsNational } from './pages/LocalVsNational';
function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Two-pass scroll: first attempt accounts for the sticky navbar height,
      // then a follow-up corrects for late-rendering content (Services, etc.)
      // that can push the target further down the page.
      const scrollToTarget = () => {
        const el = document.getElementById(id);
        if (!el) return;
        const navHeight = document.querySelector('nav')?.getBoundingClientRect().height || 80;
        const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      };
      const t1 = setTimeout(scrollToTarget, 120);
      const t2 = setTimeout(scrollToTarget, 600);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return null;
}
function AnimatedRoutes() {
  const location = useLocation();
  return <AnimatePresence mode="wait" data-id="element-0">
      <Routes location={location} key={location.pathname} data-id="element-1">
        <Route path="/" element={<PageTransition data-id="element-3">
              <Home data-id="element-4" />
            </PageTransition>} data-id="element-2" />
        <Route path="/contact" element={<PageTransition data-id="element-6">
              <Contact data-id="element-7" />
            </PageTransition>} data-id="element-5" />
        <Route path="/shop/:slug" element={<PageTransition data-id="element-9">
              <Product data-id="element-10" />
            </PageTransition>} data-id="element-8" />
        <Route path="/help" element={<PageTransition data-id="element-12">
              <HelpMeChoose data-id="element-13" />
            </PageTransition>} data-id="element-11" />
        <Route path="/about" element={<PageTransition data-id="element-15">
              <AboutPage data-id="element-16" />
            </PageTransition>} data-id="element-14" />
        <Route path="/delivery-zones" element={<PageTransition data-id="element-18">
              <DeliveryZones data-id="element-19" />
            </PageTransition>} data-id="element-17" />
        <Route path="/subcontractors" element={<PageTransition data-id="element-21">
              <Subcontractors data-id="element-22" />
            </PageTransition>} data-id="element-20" />
        <Route path="/contractor-ordering" element={<PageTransition data-id="element-24">
              <Subcontractors data-id="element-25" />
            </PageTransition>} data-id="element-23" />
        <Route path="/diy-help" element={<PageTransition data-id="element-27">
              <DiyHelp data-id="element-28" />
            </PageTransition>} data-id="element-26" />
        <Route path="/delivery-prep" element={<PageTransition data-id="element-30">
              <DeliveryPrep data-id="element-31" />
            </PageTransition>} data-id="element-29" />
        <Route path="/compare" element={<PageTransition data-id="element-33">
              <CompareMaterials data-id="element-34" />
            </PageTransition>} data-id="element-32" />
        <Route path="/calculator" element={<PageTransition data-id="element-36">
              <Calculator data-id="element-37" />
            </PageTransition>} data-id="element-35" />
        <Route path="/local-vs-national" element={<PageTransition data-id="element-39">
              <LocalVsNational data-id="element-40" />
            </PageTransition>} data-id="element-38" />
        <Route path="/track" element={<PageTransition data-id="element-42">
              <TrackOrderLookup data-id="element-43" />
            </PageTransition>} data-id="element-41" />
        <Route path="/track/:orderId" element={<PageTransition data-id="element-45">
              <TrackOrder data-id="element-46" />
            </PageTransition>} data-id="element-44" />
        <Route path="*" element={<PageTransition data-id="element-48">
              <NotFound data-id="element-49" />
            </PageTransition>} data-id="element-47" />
      </Routes>
    </AnimatePresence>;
}
function AppContent({
  isDark,
  toggleDark
}: {
  isDark: boolean;
  toggleDark: () => void;
}) {
  const location = useLocation();
  const isContractorPage = location.pathname.includes('subcontractors') || location.pathname.includes('contractor');
  return <div className={`min-h-screen flex flex-col font-sans ${isDark ? 'dark' : ''} ${isContractorPage ? 'contractor-theme' : ''}`} data-id="element-50">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-brand-orange focus:text-white focus:rounded-lg focus:font-bold focus:outline-none focus:ring-2 focus:ring-white" data-id="element-51">
        Skip to main content
      </a>
      <div className="flex-grow bg-[#fdfbf7] dark:bg-[#222222] transition-colors duration-300 pb-16 md:pb-0" id="main-content" data-id="element-52">
        <ScrollToHash data-id="element-53" />
        <AnnouncementBar data-id="element-54" />
        <Navbar isDark={isDark} toggleDark={toggleDark} data-id="element-55" />
        <AnimatedRoutes data-id="element-56" />
        <CredentialsBar data-id="element-57" />
        <Footer data-id="element-58" />
        <LiveChat data-id="element-59" />
        <StickyMobileCTA data-id="element-60" />
        <ScrollToTopButton data-id="element-61" />
        <CartDrawer data-id="element-62" />
      </div>
    </div>;
}
export function App() {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) {
      return saved === 'dark';
    }
    return true;
  });
  useEffect(() => {
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);
  const toggleDark = () => setIsDark(!isDark);
  return <I18nProvider data-id="element-63">
      <Router data-id="element-64">
        <CartProvider data-id="element-65">
          <AppContent isDark={isDark} toggleDark={toggleDark} data-id="element-66" />
        </CartProvider>
      </Router>
    </I18nProvider>;
}